import React, { useCallback, useState, useEffect } from 'react';
import { Layout, FloatButton, Tooltip } from 'antd';
import { Outlet, useNavigate } from 'react-router-dom';
import { LockOutlined, DollarOutlined, ShoppingOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import AddPayment from 'pages/payments/add-payment';
import { CLOSE_PAYMENT_MODAL, OPEN_PAYMENT_MODAL } from 'redux/reducers/constants';
import { getApi } from 'redux/sagas/getApiDataSaga';
import { setUserDetails } from 'redux/reducers/login/loginActions';
import { getAdminMenuSettings } from 'services/menuSettingsApi';
import { SERVER_IP } from 'assets/Config';
import { subscribeToMenuSettingsUpdates, subscribeToForceLogout } from '../firebase';
import { applicationLogout } from 'services/LoginServices';
import { useI18n } from 'hooks/useI18n';
import { useDeviceDetection } from 'hooks/useDeviceDetection';
import { useSecurity } from 'hooks/useSecurity';
import Sidebar from './side-bar';
import PageHeader from './page-header';
import PageLoader from './page-loader';
import { SUPER_ADMIN_EMAILS } from 'constants/app-constants';

const { Content } = Layout;

const PageLayout = () => {
	const { t } = useI18n();
	const { isMobile, isTablet } = useDeviceDetection();
	const { isEnabled, hasLockSet, lockApp } = useSecurity();
	const user = useSelector((state) => state?.loginRedux);
	const globalRedux = useSelector((state) => state?.globalRedux);
	const [paymentAddModal, setPaymentAddModal] = useState(false);
	const branches = useSelector((state) => state?.globalRedux?.branches || []);
	const selectedBranchId = useSelector((state) => state?.globalRedux?.selectedBranch?.id);
	const isSuperAdmin = SUPER_ADMIN_EMAILS.includes(user?.email);
	const isBranchSelected = branches.some((branch) => branch.id === selectedBranchId);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const adminGymId = user?.gymId || user?.id;

	useEffect(() => {
		if (!isSuperAdmin && !isBranchSelected) {
			navigate('/branches');
		}
	}, [isBranchSelected, isSuperAdmin, navigate]);

	const handleClose = useCallback(() => {
		setPaymentAddModal(false);
		dispatch({ type: CLOSE_PAYMENT_MODAL });
	}, [setPaymentAddModal, dispatch]);

	const openPaymentModal = (type) => {
		dispatch({ type: OPEN_PAYMENT_MODAL, payload: { type } });
		setPaymentAddModal(true);
	};

	const handleLockApp = () => {
		if (isEnabled && hasLockSet) {
			lockApp();
		}
	};

	// Hardcode menus for isSuperAdmin
	const superAdminMenus = ['dashboard', 'gyms', 'gym_payments', 'password-setup', 'settings', 'profile'];
	const menusToDisplay = isSuperAdmin ? superAdminMenus : user?.enabledMenus || []; // Use enabledMenus for admin users

	React.useEffect(() => {
		// Don't make API calls on mobile or tablet devices
		if (isMobile || isTablet) {
			return;
		}

		if (globalRedux?.selectedBranch?.id) {
			dispatch(getApi('GET_SERVICES', `${SERVER_IP}api/services?branchId=${globalRedux?.selectedBranch?.id}`));
			dispatch(getApi('GET_MEMBERSHIPS', `${SERVER_IP}api/memberships?branchId=${globalRedux?.selectedBranch?.id}`));
			dispatch(getApi('GET_COACHES', `${SERVER_IP}api/coaches?branchId=${globalRedux?.selectedBranch?.id}`));
		}
	}, [dispatch, globalRedux?.selectedBranch?.id, isMobile, isTablet]);

	useEffect(() => {
		let isMounted = true;

		if (isSuperAdmin || !adminGymId) {
			return undefined;
		}

		getAdminMenuSettings(adminGymId).then((menuSettings) => {
			if (!isMounted || !Array.isArray(menuSettings?.enabledMenus)) {
				return;
			}
			dispatch(setUserDetails({ enabledMenus: menuSettings.enabledMenus }));
		});

		return () => {
			isMounted = false;
		};
	}, [adminGymId, dispatch, isSuperAdmin]);

	// Silently refresh menu settings when the super admin updates them via Firestore signal.
	useEffect(() => {
		if (isSuperAdmin || !adminGymId) return;

		let isFirstSnapshot = true;
		const unsubscribe = subscribeToMenuSettingsUpdates(adminGymId, (snapshot) => {
			// onSnapshot fires immediately with the current value — skip that initial call.
			if (isFirstSnapshot) {
				isFirstSnapshot = false;
				return;
			}
			if (!snapshot.exists()) return;
			getAdminMenuSettings(adminGymId).then((menuSettings) => {
				if (Array.isArray(menuSettings?.enabledMenus)) {
					dispatch(setUserDetails({ enabledMenus: menuSettings.enabledMenus }));
				}
			});
		});

		return () => unsubscribe?.();
	}, [adminGymId, dispatch, isSuperAdmin]);

	// Force logout the gym admin when the super admin triggers it via Firestore signal.
	useEffect(() => {
		if (isSuperAdmin || !adminGymId) return;

		let isFirstSnapshot = true;
		const unsubscribe = subscribeToForceLogout(adminGymId, (snapshot) => {
			if (isFirstSnapshot) {
				isFirstSnapshot = false;
				return;
			}
			if (!snapshot.exists()) return;
			applicationLogout();
		});

		return () => unsubscribe?.();
	}, [adminGymId, isSuperAdmin]);

	useEffect(() => {
		if (isSuperAdmin) return; // Skip permission logic for super admin

		const currentPath = window.location.pathname.split('/')[1]; // Get the current menu from URL

		// Define routes that should always be accessible
		const alwaysAccessibleRoutes = ['login', 'logout'];

		// Allow access to always accessible routes
		if (alwaysAccessibleRoutes.includes(currentPath)) return;

		// Redirect to no-access page if the menu is not enabled
		// if (!menusToDisplay.includes(currentPath)) {
		// 	navigate('/no-access');
		// }
	}, [menusToDisplay, isSuperAdmin, navigate]);

	return (
		<Layout style={{ height: '100vh' }}>
			<PageLoader />
			<Sidebar />
			<Layout style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
				<PageHeader />
				<Content style={{ overflow: 'auto', flex: 1 }}>
					<Outlet />
				</Content>
			</Layout>

			{/* Floating Action Buttons */}
			<FloatButton.Group shape="circle" style={{ right: 24, bottom: 24 }}>
				{/* Add Membership Payment Button */}
				{/* <Tooltip title={t('forms.new_payment')} placement="left">
					<FloatButton
						icon={<DollarOutlined />}
						onClick={() => openPaymentModal('membership')}
					/>
				</Tooltip> */}
				
				{/* Add Product Payment Button */}
				{/* <Tooltip title={t('forms.product_payment')} placement="left">
					<FloatButton
						icon={<ShoppingOutlined />}
						onClick={() => openPaymentModal('product')}
					/>
				</Tooltip> */}
				
				{/* Instant Lock Button - Only show if security is enabled */}
				{isEnabled && hasLockSet && (
					<Tooltip title={t('security.lock_now')} placement="left">
						<FloatButton
							icon={<LockOutlined />}
							type="primary"
							onClick={handleLockApp}
						/>
					</Tooltip>
				)}
			</FloatButton.Group>

			<AddPayment {...{ paymentAddModal, setPaymentAddModal, handleClose }} />
		</Layout>
	);
};

export default PageLayout;
